import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Conditional Form Groups — component-scoped variant context.
 *
 * Holds the currently-selected variant guid for a single form-fill UI. Provided at the
 * COMPONENT level (e.g. `providers: [VariantContextService]` on session-form-page-steps),
 * NOT root. Two simultaneously-open form modals or wizards must each have their own
 * instance — `providedIn: 'root'` would cause cross-contamination between them.
 *
 * Set by the variant selector at the top of the form; read by deep child components
 * (detail-info-list, etc.) without prop-drilling. `null` means "no variant selected yet"
 * (the wizard typically hides itself in that state for forms that have variants).
 */
@Injectable()
export class VariantContextService {
  private readonly subject = new BehaviorSubject<string | null>(null);

  /** Live stream of the currently-selected variant guid. */
  public readonly selectedVariantGuid$: Observable<string | null> = this.subject.asObservable();

  /** Snapshot of the current selection — handy when imperatively recomputing visibility. */
  public get selectedVariantGuid(): string | null {
    return this.subject.value;
  }

  /** Update the active variant; emits to subscribers. */
  public setSelectedVariantGuid(variantGuid: string | null): void {
    if (this.subject.value === variantGuid) return; // no-op on same value
    this.subject.next(variantGuid);
  }
}
